import { DestroyRef, Injectable, inject } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { ActivatedRoute, Router } from '@angular/router'

import { isEmpty as _isEmpty } from 'lodash/fp'

import { combineLatest, filter, take } from 'rxjs'

import {
  ANALYTICS_EVENTS,
  ActionWrap,
  ProcessAction,
  ProcessActionTriggerEvent,
  ProcessActionType,
  ResolvedAction,
  TemplateConfigInterface,
  TemplateConfigInterfaceFromCollection,
  callAiTrigger,
  closeModal,
  displayTrigger$,
  eventDispatchReady$,
  expressionHasDynamicVariables,
  getActionsByTemplateConfigAndTrigger$,
  getActionsByTemplateConfigAndTriggerSync,
  getActiveUiFlowSessionId,
  getOpenModal,
  getProcessInstanceUuidFromUrl,
  getProcessValueSync,
  getRootProcessInstancesUuids,
  getRootTemplateConfigForId,
  getUrlQueryParamValue,
  isOnLoadTriggered,
  loadTrigger$,
  markOnLoadTriggered,
  navigationStack,
  notifyOnDisplayCompleted,
  notifyOnDisplayStarted,
  notifyOnLoadCompleted,
  notifyOnLoadStarted,
  parseJson,
  pushAnalyticsData,
  resolveAnalyticsName,
  resolveAnalyticsPayload,
  resolveAnalyticsScreen,
  runActions,
  setHistoryReplayWrap,
  triggerDisplayEvent,
  waitForOnLoadCompletion,
} from '@flowx/core-sdk'
import type { TemplateConfig } from '@flowx/core-sdk'

import { FlxFormsService } from './flx-forms.service'

@Injectable({
  providedIn: 'root',
})
export class FlxActionService {
  #formService = inject(FlxFormsService)
  #router = inject(Router)
  #activatedRoute = inject(ActivatedRoute)

  constructor() {
    setHistoryReplayWrap((action, fn, tc) => this.buildAction(action, fn, tc))
  }

  buildAction(
    action: ProcessAction,
    actionFn: (
      params?: Record<string, any> | FormData,
      targetTemplateConfig?: TemplateConfigInterfaceFromCollection
    ) => Promise<any>,
    targetTemplateConfig?: TemplateConfigInterfaceFromCollection
  ): (params?: Record<string, any> | FormData) => Promise<void> {
    return async (params?: Record<string, any> | FormData) => {
      switch (action.type) {
        case ProcessActionType.START_PROJECT:
        case ProcessActionType.ACTION: {
          const formControlsToValidate = this.#formService.getFormControlsAndNames(
            action?.params?.formsToSubmit,
            targetTemplateConfig.prototypeInstanceMeta,
            action?.reusableUiTemplateContext
          )

          const customComponentsToValidate = this.#formService.getValidateComponents(
            action?.params?.formsToSubmit,
            action?.reusableUiTemplateContext
          )

          if (!_isEmpty(formControlsToValidate)) {
            const validatedForms = this.#formService.submittedForms$.value
            this.#formService.submittedForms$.next({
              ...validatedForms,
              ...[...formControlsToValidate, ...customComponentsToValidate]
                ?.map(({ name }) => name)
                .reduce(
                  (acc, formUuid) => ({
                    ...acc,
                    [formUuid]: true,
                  }),
                  {}
                ),
            })
          }

          const formControlsValid = [
            ...formControlsToValidate.map(({ control }) => control),
            ...customComponentsToValidate.map(({ control }) => control),
          ].every(
            (formControl) =>
              // also check if form is still defined (might be deleted after button validation action is registered)
              formControl.status === 'VALID' || formControl.status === 'DISABLED'
          )
          if (
            (_isEmpty(formControlsToValidate) && _isEmpty(customComponentsToValidate)) ||
            formControlsValid
          ) {
            await actionFn(params, targetTemplateConfig)
            if (!_isEmpty(formControlsToValidate)) {
              const validatedForms = this.#formService.submittedForms$.value
              this.#formService.submittedForms$.next({
                ...validatedForms,
                ...[...formControlsToValidate, ...customComponentsToValidate]
                  ?.map(({ name }) => name)
                  .reduce(
                    (acc, formUuid) => ({
                      ...acc,
                      [formUuid]: false,
                    }),
                    {}
                  ),
              })
              formControlsToValidate.forEach(({ control }) =>
                control.markAsPristine({ onlySelf: true })
              )
            }
          }
          this.#handleAnalytics(action, targetTemplateConfig)
          break
        }

        case ProcessActionType.UPLOAD: {
          const uiFlowSessionUuid = getActiveUiFlowSessionId()
          const processInstancesUuids = getRootProcessInstancesUuids()
          const isUiFlow = !!uiFlowSessionUuid && !processInstancesUuids.length

          // Core sends this action's event once the outcome is known.
          if (isUiFlow) {
            await actionFn(params, targetTemplateConfig)
            break
          }

          const formControlsToValidate = this.#formService.getFormControlsAndNames(
            action?.params?.formsToSubmit,
            targetTemplateConfig.prototypeInstanceMeta,
            action?.reusableUiTemplateContext
          )

          const customComponentsToValidate = this.#formService.getValidateComponents(
            action?.params?.formsToSubmit,
            action?.reusableUiTemplateContext
          )

          if (!_isEmpty(formControlsToValidate)) {
            const validatedForms = this.#formService.submittedForms$.value
            this.#formService.submittedForms$.next({
              ...validatedForms,
              ...[...formControlsToValidate, ...customComponentsToValidate]
                ?.map(({ name }) => name)
                .reduce(
                  (acc, formUuid) => ({
                    ...acc,
                    [formUuid]: true,
                  }),
                  {}
                ),
            })
          }
          const formControlsValid = [
            ...formControlsToValidate.map(({ control }) => control),
            ...customComponentsToValidate.map(({ control }) => control),
          ].every(
            (formControl) =>
              // also check if form is still defined (might be deleted after button validation action is registered)
              formControl.status === 'VALID' || formControl.status === 'DISABLED'
          )
          if (_isEmpty(formControlsToValidate) || formControlsValid) {
            await actionFn(params, targetTemplateConfig)
            if (!_isEmpty(formControlsToValidate)) {
              const validatedForms = this.#formService.submittedForms$.value
              this.#formService.submittedForms$.next({
                ...validatedForms,
                ...[...formControlsToValidate, ...customComponentsToValidate]
                  ?.map(({ name }) => name)
                  .reduce(
                    (acc, formUuid) => ({
                      ...acc,
                      [formUuid]: false,
                    }),
                    {}
                  ),
              })
              formControlsToValidate.forEach(({ control }) =>
                control.markAsPristine({ onlySelf: true })
              )
            }
          }
          break
        }
        case ProcessActionType.DISMISS: {
          // A DISMISS owned by a process (its config carries a processInstanceUuid) always
          // runs the process dismiss routine — even when that process is rendered inside a
          // UI-flow modal. Only a UI-flow-owned DISMISS closes the modal.
          const isProcessOwnedDismiss = !!targetTemplateConfig?.processInstanceUuid
          if (
            !isProcessOwnedDismiss &&
            getActiveUiFlowSessionId() !== null &&
            getOpenModal() &&
            getUrlQueryParamValue('modalId') !== null
          ) {
            if (navigationStack.currentEntry?.ephemeral?.modalId) {
              navigationStack.pop()
            }
            closeModal()
            triggerDisplayEvent('modal-dismiss')
            break
          }

          if (isProcessOwnedDismiss || getProcessInstanceUuidFromUrl() !== null) {
            await actionFn(params)
            this.#handleAnalytics(action, targetTemplateConfig)
          }
          break
        }
        case ProcessActionType.EXTERNAL: {
          await actionFn(params, targetTemplateConfig)
          this.#handleAnalytics(action, targetTemplateConfig)
          break
        }
        case ProcessActionType.DOWNLOAD: {
          // Core sends this action's event once the outcome is known.
          await actionFn(params, targetTemplateConfig)
          break
        }
        case ProcessActionType.SAVE_LOCAL_VARIABLE: {
          await actionFn(params, targetTemplateConfig)
          this.#handleAnalytics(action, targetTemplateConfig)
          break
        }
        case ProcessActionType.START_PROCESS:
        case ProcessActionType.START_WORKFLOW:
        case ProcessActionType.NAVIGATE_TO: {
          const formControlsToValidate = this.#formService.getFormControlsAndNames(
            action?.params?.formsToSubmit,
            targetTemplateConfig.prototypeInstanceMeta,
            action?.reusableUiTemplateContext
          )
          const customComponentsToValidate = this.#formService.getValidateComponents(
            action?.params?.formsToSubmit,
            action?.reusableUiTemplateContext
          )

          if (!_isEmpty(formControlsToValidate)) {
            const validatedForms = this.#formService.submittedForms$.value
            this.#formService.submittedForms$.next({
              ...validatedForms,
              ...[...formControlsToValidate, ...customComponentsToValidate]
                ?.map(({ name }) => name)
                .reduce(
                  (acc, formUuid) => ({
                    ...acc,
                    [formUuid]: true,
                  }),
                  {}
                ),
            })
          }

          const formControlsValid = [
            ...formControlsToValidate.map(({ control }) => control),
            ...customComponentsToValidate.map(({ control }) => control),
          ].every(
            (formControl) =>
              // also check if form is still defined (might be deleted after button validation action is registered)
              formControl.status === 'VALID' || formControl.status === 'DISABLED'
          )
          if (
            (_isEmpty(formControlsToValidate) && _isEmpty(customComponentsToValidate)) ||
            formControlsValid
          ) {
            await actionFn(params, targetTemplateConfig)

            if (action.type === ProcessActionType.NAVIGATE_TO) {
              // Angular-specific: sync Router internal state with the URL already updated by core
              const currentParams: Record<string, string> = {}
              new URL(window.location.href).searchParams.forEach((v, k) => {
                currentParams[k] = v
              })
              this.#router.navigate([], {
                relativeTo: this.#activatedRoute,
                queryParams: currentParams,
                replaceUrl: true,
              })
            }

            if (!_isEmpty(formControlsToValidate)) {
              formControlsToValidate.forEach(({ control }) =>
                control.markAsPristine({ onlySelf: true })
              )
            }
          }
          this.#handleAnalytics(action, targetTemplateConfig)
          break
        }

        case ProcessActionType.UI_EVENT: {
          await actionFn(params, targetTemplateConfig)
          break
        }

        case ProcessActionType.AI_TRIGGER: {
          // Dispatched here (not via core buildAction) to keep chat.service out of
          // the core actions.service import graph.
          await callAiTrigger(
            {
              workflowAppId: action.resourceReference?.appId,
              workflowResDefId: action.resourceReference?.resourceDefinitionId,
              triggerFlowxUuid: action.resourceReference?.flowxUuid,
              params: action.params?.aiTriggerParams,
            },
            targetTemplateConfig as TemplateConfig
          )
          break
        }
      }
    }
  }

  /**
   * Runs a pre-fetched list of actions in `order`. Delegates the dispatch loop to
   * core's `runActions`, passing this service's `buildAction` as the framework-specific
   * wrap (form gating, analytics, router sync, AI trigger dispatch).
   */
  async runActions(
    actions: ResolvedAction[],
    templateConfig: TemplateConfigInterfaceFromCollection,
    params?: Record<string, any> | FormData
  ): Promise<void> {
    const wrap: ActionWrap = (a, f, tc) => this.buildAction(a, f, tc)
    return runActions(actions, templateConfig, wrap, params)
  }

  /**
   * Runs process onLoad: dispatches the ordered LOAD action list (workflow awaited, UI_EVENT
   * and AI_TRIGGER fire-and-forget) via runActions, then notifies completion. Use from Page,
   * Container (zone), or TabBar when they are user task roots.
   *
   * @param config - Template config (page, zone, or tab bar)
   * @param destroyRef - Component's DestroyRef for subscription cleanup
   * @param shouldRun - Predicate to run (e.g. isUserTaskRoot, or isZoneConfig + isUserTaskRoot for zones)
   */
  runOnLoadEvent(
    config: TemplateConfigInterface,
    destroyRef: DestroyRef,
    shouldRun: (config: TemplateConfigInterface) => boolean
  ): void {
    const executeOnLoad = (loadActions: ResolvedAction[]): void => {
      if (isOnLoadTriggered(config.flowxUuid)) {
        return
      }

      markOnLoadTriggered(config.flowxUuid)
      notifyOnLoadStarted(config.flowxUuid)

      this.runActions(loadActions, config as TemplateConfigInterfaceFromCollection).finally(() =>
        notifyOnLoadCompleted(config.flowxUuid)
      )
    }

    // Initial dispatch: wait for the readiness barrier AND for the node's LOAD actions
    // to land. Nested/tab nodes resolve their actions via getNodeDetails AFTER mount, so
    // a synchronous read can capture an empty set; the reactive stream re-emits once they
    // arrive. (Page/zone roots already have their actions, so this fires synchronously.)
    combineLatest([
      eventDispatchReady$,
      getActionsByTemplateConfigAndTrigger$(
        config as TemplateConfig,
        ProcessActionTriggerEvent.LOAD
      ),
    ])
      .pipe(
        filter(([, loadActions]) => loadActions.length > 0),
        take(1),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(([, loadActions]) => {
        if (shouldRun(config)) {
          executeOnLoad(loadActions)
        }
      })

    // Re-fire onLoad when intent navigation triggers it on already-mounted components
    loadTrigger$.pipe(takeUntilDestroyed(destroyRef)).subscribe(({ destinationViewId }) => {
      if (destinationViewId !== config.flowxUuid) {
        return
      }
      executeOnLoad(
        getActionsByTemplateConfigAndTriggerSync(
          config as TemplateConfig,
          ProcessActionTriggerEvent.LOAD
        )
      )
    })
  }

  /**
   * Runs process onDisplay: dispatches the ordered DISPLAY action list via runActions.
   * Unlike onLoad, this fires every time — on initial mount and whenever displayTrigger$
   * emits (navigation, modal-dismiss).
   *
   * @param config - Template config (page, zone, or tab bar)
   * @param destroyRef - Component's DestroyRef for subscription cleanup
   * @param shouldRun - Predicate to run (e.g. isUserTaskRoot, or isZoneConfig + isUserTaskRoot for zones)
   */
  runOnDisplayEvent(
    config: TemplateConfigInterface,
    destroyRef: DestroyRef,
    shouldRun: (config: TemplateConfigInterface) => boolean
  ): void {
    let destroyed = false
    destroyRef.onDestroy(() => {
      destroyed = true
    })

    let inFlight = false
    let initFiredWithModal = false

    const executeDisplay = (): void => {
      if (inFlight) {
        return
      }
      inFlight = true
      notifyOnDisplayStarted(config.flowxUuid)

      const displayActions = getActionsByTemplateConfigAndTriggerSync(
        config as TemplateConfig,
        ProcessActionTriggerEvent.DISPLAY
      )

      this.runActions(displayActions, config as TemplateConfigInterfaceFromCollection).finally(
        () => {
          inFlight = false
          notifyOnDisplayCompleted(config.flowxUuid)
        }
      )
    }

    // Fire on initial mount.
    // If a modal is already open (refresh-with-modal scenario), record it so we can
    // suppress the redundant 'modal-dismiss' re-fire — the page's display state was
    // already established by this initial call.
    const fireInitialDisplay = (): void => {
      if (getOpenModal()) {
        initFiredWithModal = true
      }
      waitForOnLoadCompletion(config.flowxUuid).then(() => {
        if (!destroyed) {
          executeDisplay()
        }
      })
    }

    // See runOnLoadEvent for readiness + late-arriving-actions rationale. Wait for the
    // readiness barrier AND for the node's DISPLAY actions to land before the initial fire.
    combineLatest([
      eventDispatchReady$,
      getActionsByTemplateConfigAndTrigger$(
        config as TemplateConfig,
        ProcessActionTriggerEvent.DISPLAY
      ),
    ])
      .pipe(
        filter(([, displayActions]) => displayActions.length > 0),
        take(1),
        takeUntilDestroyed(destroyRef)
      )
      .subscribe(() => {
        if (shouldRun(config)) {
          fireInitialDisplay()
        }
      })

    // Re-fire on navigation/modal-dismiss signals.
    // For modal-dismiss, triggerDisplayEvent scopes destinationViewId to the root behind
    // the modal (resolved from the URL viewId), so only components under that root re-fire,
    // restoring the page that was behind the modal without waking other mounted roots/globals.
    // It broadcasts (destinationViewId undefined) only as a fallback when the root can't be resolved.
    displayTrigger$.pipe(takeUntilDestroyed(destroyRef)).subscribe({
      next: ({ source, destinationViewId }) => {
        const openModal = getOpenModal()
        if (source === 'modal-dismiss' || !openModal) {
          // Only fire for the navigation destination (if specified).
          // Accept if destinationViewId matches this component directly OR matches its
          // root template — so zones/containers inside the destination page also fire.
          if (
            destinationViewId &&
            destinationViewId !== config.flowxUuid &&
            getRootTemplateConfigForId(config.flowxUuid)?.flowxUuid !== destinationViewId
          ) {
            return
          }
          // After a refresh-with-modal, the initial mount already ran executeDisplay().
          // Suppress the first 'modal-dismiss' re-fire to avoid a duplicate workflow start.
          // Reset the flag so subsequent modal open/dismiss cycles fire normally.
          if (source === 'modal-dismiss' && initFiredWithModal) {
            initFiredWithModal = false
            return
          }
          waitForOnLoadCompletion(config.flowxUuid).then(() => {
            if (!destroyed) {
              executeDisplay()
            }
          })
        }
      },
    })
  }

  #handleAnalytics(
    action: ProcessAction,
    targetTemplateConfig?: TemplateConfigInterfaceFromCollection
  ): void {
    const analyticsValue = action?.params?.analytics
    const analyticsCustomPayload = action?.params?.analyticsCustomPayload
    if (analyticsValue) {
      const {
        componentIdentifier,
        displayOptions: { flowxProps },
      } = targetTemplateConfig || {}

      resolveAnalyticsScreen().subscribe((screenAnalytics) => {
        const label = expressionHasDynamicVariables(flowxProps?.label || flowxProps?.text)
          ? getProcessValueSync(flowxProps?.label || flowxProps?.text, targetTemplateConfig, {
              skipLocalization: false,
              keepStringValueQuotes: false,
              analytics: true,
            })
          : flowxProps?.label || flowxProps?.text

        const customPayload =
          analyticsCustomPayload &&
          resolveAnalyticsPayload(analyticsCustomPayload, targetTemplateConfig)

        pushAnalyticsData({
          type: ANALYTICS_EVENTS.ACTION,
          value: resolveAnalyticsName(analyticsValue, targetTemplateConfig),
          screen: screenAnalytics,
          component: componentIdentifier,
          label,
          customPayload: parseJson(customPayload as string | undefined),
        })
      })
    }
  }
}
